/**
 * Logger Utility
 *
 * Conditional logging that only outputs in development mode.
 * Prevents sensitive data from leaking in production logs.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const isDev = import.meta.env.DEV

/**
 * Logger that only outputs in development mode
 * In production, all logs are silently dropped except errors
 */
export const logger = {
  /**
   * Debug logging - development only
   */
  debug: (...args: unknown[]): void => {
    if (isDev) {
      console.log('[DEBUG]', ...args)
    }
  },

  /**
   * Info logging - development only
   */
  info: (...args: unknown[]): void => {
    if (isDev) {
      console.log('[INFO]', ...args)
    }
  },

  /**
   * Warning logging - development only
   */
  warn: (...args: unknown[]): void => {
    if (isDev) {
      console.warn('[WARN]', ...args)
    }
  },

  /**
   * Error logging - always outputs (but sanitized in production)
   */
  error: (...args: unknown[]): void => {
    if (isDev) {
      console.error('[ERROR]', ...args)
    } else {
      // In production, only log error message without potentially sensitive data
      const sanitized = args.map(arg => {
        if (arg instanceof Error) {
          return arg.message
        }
        if (typeof arg === 'string') {
          return arg
        }
        return '[object]'
      })
      console.error('[ERROR]', ...sanitized)
    }
  },

  /**
   * Structured logging with metadata
   */
  log: (level: LogLevel, message: string, data?: Record<string, unknown>): void => {
    if (!isDev && level !== 'error') return

    const entry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...(isDev && data ? { data } : {}),
    }

    const method = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log
    method(JSON.stringify(entry))
  },
}

/**
 * Simple dev-only log function
 * Use this for quick debugging that should never appear in production
 */
export const devLog = isDev ? console.log.bind(console) : () => {}

/**
 * Simple dev-only warn function
 */
export const devWarn = isDev ? console.warn.bind(console) : () => {}

export default logger
